import React, { useState } from "react";
import styles from "./sale.module.scss";
import { dealNameColor } from "../../utils/color";
import icons from "../../assets/images/icons";
import { getPhotoUrl } from "../../common/utils/getPhotoUrl";
import legacy from "../../utils/legacy";
import { OwnerVerifiedGradientBadge } from "../badge/OwnerVerifiedBadge";

function trimProvince(address) {
  if (!address) return "";
  const parts = address.split(" ");
  if (parts.length <= 1) return address;
  return parts.slice(1).join(" ");
}

function getTimeAgo(creationDate) {
  const now = new Date();
  const created = new Date(creationDate);
  const diffSec = Math.floor((now - created) / 1000);

  const units = [
    { label: "년", seconds: 60 * 60 * 24 * 365 },
    { label: "개월", seconds: 60 * 60 * 24 * 30 },
    { label: "주", seconds: 60 * 60 * 24 * 7 },
    { label: "일", seconds: 60 * 60 * 24 },
    { label: "시간", seconds: 60 * 60 },
    { label: "분", seconds: 60 },
  ];

  for (const unit of units) {
    const amount = Math.floor(diffSec / unit.seconds);
    if (amount > 0) return `${amount}${unit.label} 전`;
  }

  return "";
}

const TYPE_LIST = [
  "대", "전", "답", "임야", "과수원", "목장용지", "광천지", "염전",
  "공장용지", "학교용지", "주차장", "주유소용지", "창고용지", "도로",
  "철도용지", "제방", "하천", "구거", "유지", "양어장", "수도용지",
  "공원", "체육용지", "유원지", "종교용지", "사적지", "묘지", "잡종지",
];

function safeRound(value, digits = 0, fallback = 0.0) {
  if (value == null || isNaN(value)) return fallback;
  const multiplier = Math.pow(10, digits);
  return Math.round(value * multiplier) / multiplier;
}

function formatFloorByEstateType(randType, dealType, floorType, floorTotal, floorCnt, underFloor) {
  if (randType === 16) return "";
  if (
    [0, 1, 2, 7, 8, 10, 11, 18].includes(randType) ||
    ([4, 6].includes(randType) && dealType !== 0)
  ) {
    if (floorType == null || floorType < 0) {
      return `${floorCnt} / ${floorTotal}층`;
    }
    const floorTypes = { 0: "고", 1: "중", 2: "저" };
    return floorTypes[floorType] || "";
  }
  return `${floorCnt}F / B${underFloor}`;
}

function formatAreaByEstateType(randType, dealType, landArea, floorArea, supplyArea) {
  if (randType === 16) {
    return { mainArea: safeRound(landArea, 0, 0.0), subArea: safeRound(floorArea, 0, 0.0) };
  }
  if (
    [0, 1, 2, 7, 8, 10, 11, 18].includes(randType) ||
    ([4, 6].includes(randType) && dealType !== 0)
  ) {
    return { mainArea: safeRound(supplyArea, 0, 0.0), subArea: safeRound(floorArea, 0, 0.0) };
  }
  return { mainArea: safeRound(landArea, 0, 0.0), subArea: safeRound(floorArea, 0, 0.0) };
}

function formatAreaUnits(randType) {
  if ([2, 7, 8, 10, 11, 18].includes(randType))
    return { mainAreaUnit: "계약", subAreaUnit: "전용" };
  if (randType === 0) return { mainAreaUnit: "공급", subAreaUnit: "전용" };
  if (randType === 1) return { mainAreaUnit: "전용", subAreaUnit: "계약" };
  if ([3, 4, 5, 6, 12, 15, 17, 18, 19].includes(randType))
    return { mainAreaUnit: "대지", subAreaUnit: "연" };
  return { mainAreaUnit: null, subAreaUnit: null };
}

// ── 매물 정보 행 (공유 렌더 함수) ──
const PropertyInfo = ({ item, tag, showAddress = true }) => {
  const { mainArea, subArea } = formatAreaByEstateType(
    item.randType, item.dealType, item.landArea, item.floorArea, item.supplyArea
  );
  const { mainAreaUnit, subAreaUnit } = formatAreaUnits(item.randType);
  const floorText = formatFloorByEstateType(
    item.randType, item.dealType, item.floorType, item.floorTotal, item.floorCnt, item.underFloor
  );
  const mainAreaHtml = legacy.to_area(mainArea);
  const subAreaHtml = legacy.to_area(subArea);
  const title = trimProvince(item.address);

  return (
    <div className={styles.estateInfo}>
      <div className={styles.topRow}>
        <div className={styles.tagRow}>
          <span className={styles.estateTag}>{item.tag || tag}</span>
          {item.ownerVerified && <OwnerVerifiedGradientBadge />}
        </div>
        <span className={styles.estateTime}>{getTimeAgo(item.creationDate)}</span>
      </div>
      {showAddress && title && <div className={styles.estateTitle}>{title}</div>}
      <div className={styles.priceRow}>
        <span className={styles.dealType} style={{ color: dealNameColor(item.dealType) }}>
          {item.dealType}{" "}
        </span>
        <span className={styles.estatePrice}>{item.price}</span>
      </div>
      <div className={styles.estateDetails}>
        {item.floor ? (
          <>
            <div className={styles.areaDetails}>
              <span>{mainAreaUnit}</span>
              <span dangerouslySetInnerHTML={{ __html: mainAreaHtml }} />
              <span>∙</span>
              <span>{subAreaUnit}</span>
              <span dangerouslySetInnerHTML={{ __html: subAreaHtml }} />
            </div>
            <div className={styles.floorDivider} />
            <span>{floorText}</span>
          </>
        ) : (
          <div className={styles.areaDetails}>
            <span>{mainAreaUnit}</span>
            <span dangerouslySetInnerHTML={{ __html: mainAreaHtml }} />
            <span>∙</span>
            <span>{TYPE_LIST[item.landPurpose]}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ── 프로필 바 ──
const ProfileBar = ({ item }) => {
  const agentImage = getPhotoUrl(item.agentPhoto, "agent");
  return (
    <div className={styles.profileBarWrapper}>
      <div className={styles.profileBar}>
        <img src={agentImage} alt={item.agentName} className={styles.agentAvatar} />
        <div className={styles.profileContent}>
          <div className={styles.agentNameGroup}>
            <span className={styles.agentName}>{item.agentName}</span>
            <span className={styles.agentPosition}>{item.agentPosition}</span>
          </div>
          <div className={styles.profileDivider} />
          <span className={styles.agentOfficeName}>{item.agentOfficeName}</span>
          {item.agentTier === "pro" && (
            <img className={styles.agentTier} src={icons.nameCard.pro} alt="pro" />
          )}
        </div>
      </div>
    </div>
  );
};

// ── 대표 카드 ──
const EstateCard = ({
  onClick = () => {},
  onOpenDetail,
  onMoreClick = () => {},
  onExpand,
  onHover = () => {},
  onLeave = () => {},
  image,
  dealType,
  price,
  tag,
  estateTypeTag,
  randType,
  buildingName,
  address,
  landPurpose,
  landArea,
  supplyArea,
  floorArea,
  floorCnt,
  floorTotal,
  underFloor,
  floorType,
  floor,
  more = false,
  maxPrice,
  minPrice,
  agentPhoto,
  agentName,
  agentOfficeName,
  agentPosition,
  agentTier,
  ownerVerified,
  suid,
  lat,
  lng,
  creationDate,
  groupCount = 1,
  list = [],
  initialExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(initialExpanded);
  const cardRef = React.useRef(null);
  const maemulImage = getPhotoUrl(image, "building");

  // initialExpanded가 true이고 list가 아직 없으면 자동으로 상세 로드
  React.useEffect(() => {
    if (initialExpanded && more && list.length === 0) {
      onMoreClick();
    }
  }, [initialExpanded]);

  const toggleExpand = async () => {
    if (!expanded && list.length === 0) {
      await onMoreClick();
    }
    const next = !expanded;
    setExpanded(next);
    onExpand?.(next);
    // 펼칠 때 대표 매물이 스크롤 상단 근처에 오도록 (상단 여백 12px)
    if (next && cardRef.current) {
      requestAnimationFrame(() => {
        const container = cardRef.current.closest("[class*='bottomSheetContent']") || cardRef.current.parentElement;
        if (container) {
          const top = cardRef.current.offsetTop - container.offsetTop - 12;
          container.scrollTo({ top, behavior: "smooth" });
        }
      });
    }
  };

  // 메인 카드 데이터를 item 객체로 구성 (PropertyInfo에 전달)
  const mainItem = {
    tag, estateTypeTag, ownerVerified, creationDate, address,
    dealType, price, floor, randType, landPurpose,
    landArea, floorArea, supplyArea, floorCnt, floorTotal,
    underFloor, floorType,
    agentPhoto, agentName, agentOfficeName, agentPosition, agentTier,
  };

  return (
    <div ref={cardRef} onMouseEnter={() => onHover(lat, lng)} onMouseLeave={() => onLeave()}>
      <div className={styles.estateCard}>
        {/* 메인 컨텐츠 영역 */}
        <div
          className={styles.estateContent}
          onClick={() => onClick(suid)}
        >
          {maemulImage && <div className={styles.estateImage} style={{ backgroundImage: `url(${maemulImage})` }} />}
          <PropertyInfo item={mainItem} tag={tag} showAddress={true} />
        </div>

        {/* 동일매물 버튼 또는 프로필 바 */}
        {more ? (
          <div className={styles.duplicateButtonWrapper}>
            <div className={styles.duplicateButton} onClick={toggleExpand}>
              <span className={styles.duplicateButtonText}>
                {expanded ? "간략히 보기" : `동일매물 ${groupCount}개 확인`}
              </span>
              <img
                src={expanded ? icons.arrow.up.black : icons.arrow.down.black}
                className={styles.caretIcon}
                alt=""
              />
            </div>
          </div>
        ) : (
          <ProfileBar item={mainItem} />
        )}

        <div className={styles.cardDivider} />
      </div>

      {/* 펼침 영역 — 중개사별 그룹 */}
      {expanded && list.length > 0 && (
        <div className={styles.expandedArea}>
          {list.map((agentGroup, idx) => {
            const agSubItems = agentGroup.agentGroupItems || [];
            const agCount = agentGroup.agentGroupCount || 1;
            const hasAgSub = agSubItems.length > 0;

            return (
              <AgentGroupCardInline
                key={`${agentGroup.suid}_${idx}`}
                item={agentGroup}
                subItems={agSubItems}
                agentCount={agCount}
                hasSubItems={hasAgSub}
                onClick={onOpenDetail || onClick}
                tag={tag}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── 중개사별 그룹 카드 (동일매물 펼침 시) ──
const AgentGroupCardInline = ({
  item, subItems, agentCount, hasSubItems,
  onClick, tag,
}) => {
  const [agentExpanded, setAgentExpanded] = useState(false);
  const agentCardRef = React.useRef(null);
  const itemImage = getPhotoUrl(item.image, "building");

  return (
    <div className={styles.agentGroupCard} ref={agentCardRef}>
      {/* 대표 매물 */}
      <div className={styles.estateContent} onClick={() => onClick(item.suid)}>
        {itemImage && <div className={styles.estateImage} style={{ backgroundImage: `url(${itemImage})` }} />}
        <PropertyInfo item={item} tag={tag} showAddress={true} />
      </div>

      {/* 프로필 바 */}
      <ProfileBar item={item} />

      {/* 동일 중개사 매물 더보기 */}
      {hasSubItems && (
        <>
          <div className={styles.innerDivider} />
          <div className={styles.agentMoreButtonWrapper}>
            <div
              className={styles.agentMoreButton}
              onClick={() => {
                setAgentExpanded((prev) => {
                  const next = !prev;
                  if (next && agentCardRef.current) {
                    requestAnimationFrame(() => {
                      const container = agentCardRef.current.closest("[class*='bottomSheetContent']") || agentCardRef.current.parentElement;
                      if (container) {
                        const top = agentCardRef.current.offsetTop - container.offsetTop - 12;
                        container.scrollTo({ top, behavior: "smooth" });
                      }
                    });
                  }
                  return next;
                });
              }}
            >
              <span className={styles.agentMoreButtonText}>
                {agentExpanded ? "간략히 보기" : `이 중개사 매물 ${subItems.length}건 더보기`}
              </span>
              <img
                src={agentExpanded ? icons.arrow.up.black : icons.arrow.down.black}
                className={styles.caretIcon}
                alt=""
              />
            </div>
          </div>
        </>
      )}

      {/* 하위 매물 */}
      {agentExpanded &&
        subItems.map((sub, i) => (
          <div key={`${sub.suid}_${i}`} className={styles.subItemArea}>
            <div className={styles.innerDivider} />
            <div className={styles.subItemContent} onClick={() => onClick(sub.suid)}>
              {getPhotoUrl(sub.image, "building") && (
                <div
                  className={styles.subItemImage}
                  style={{ backgroundImage: `url(${getPhotoUrl(sub.image, "building")})` }}
                />
              )}
              <PropertyInfo item={sub} tag={tag} showAddress={false} />
            </div>
          </div>
        ))}
    </div>
  );
};

// ── 스켈레톤 ──
export const EstateCardSkeleton = () => (
  <div className={styles.estateCard}>
    <div className={styles.estateContent}>
      <div className={styles.skeletonImage} />
      <div className={styles.skeletonInfo}>
        <div className={styles.skeletonLine} style={{ width: "60%" }} />
        <div className={styles.skeletonLine} style={{ width: "80%" }} />
        <div className={styles.skeletonLine} style={{ width: "50%" }} />
        <div className={styles.skeletonLine} style={{ width: "70%" }} />
      </div>
    </div>
    <div className={styles.skeletonProfile}>
      <div className={styles.skeletonAvatar} />
      <div className={styles.skeletonLine} style={{ width: "60%", flex: 1 }} />
    </div>
    <div className={styles.cardDivider} />
  </div>
);

export default EstateCard;
